import moment from 'moment-timezone';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AuthenticatedUserContextType,
  deriveSupplierScopedUserFields,
  useDefaultUserContext,
  UserContext,
  UserContextType,
} from './user-context';
import { AuthStatus } from '@/enums';

type OverrideProps = Partial<
  Omit<
    AuthenticatedUserContextType,
    | 'authStatus'
    | 'checkAuthStatus'
    | 'isGuestMode'
    | 'previousAuthStatus'
    | 'refetch'
  >
>;
export type UserOverridesContextType = OverrideProps;
interface UserOverridesSetterContextType {
  setOverrides: (overrides: OverrideProps) => void;
}

export const UserOverridesContext = createContext<UserOverridesContextType>({});
const UserOverridesSetterContext =
  createContext<UserOverridesSetterContextType>({
    setOverrides: () => {},
  });

export const useUserOverrides = (overrides: OverrideProps) => {
  const { setOverrides } = useContext(UserOverridesSetterContext);

  useEffect(() => setOverrides(overrides), [overrides, setOverrides]);

  // Clear overrides when the consumer of this hook unmounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => setOverrides({}), []);
};

/** Provides override values for UserContext, and a setter for those overrides */
export const UserOverridesContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [overrides, setOverrides] = useState<OverrideProps>({});
  return (
    <UserOverridesContext.Provider value={overrides}>
      <UserOverridesSetterContext.Provider value={{ setOverrides }}>
        {children}
      </UserOverridesSetterContext.Provider>
    </UserOverridesContext.Provider>
  );
};

interface Props {
  children?: React.ReactNode;
}

/** Provides the authenticated user context + any overrides that may be active */
export const UserContextWithOverridesProvider: React.FC<Props> = ({
  children,
}) => {
  const baseUserContext = useDefaultUserContext();
  const overrides = useContext(UserOverridesContext);

  /*
   * Without overrides, we set the default timezone to the restaurant's
   * timezone to ensure formatted delivery dates are correct from the selected
   * restaurant's perspective.
   *
   * Maintain this behavior with overrides by adjusting if the selected
   * restaurant relationship is overridden.
   */
  useEffect(() => {
    if ('selectedRestaurantRelationship' in overrides) {
      const timezone = overrides.selectedRestaurantRelationship?.timezone;
      if (timezone) {
        moment.tz.setDefault(timezone);
      }
    }
    return () => {
      if ('selectedRestaurantRelationship' in baseUserContext) {
        const baseTimezone =
          baseUserContext.selectedRestaurantRelationship?.timezone;
        if (baseTimezone) {
          moment.tz.setDefault(baseTimezone);
        }
      }
    };
  }, [overrides, baseUserContext]);

  const value = useMemo<UserContextType>(() => {
    if (baseUserContext.authStatus !== AuthStatus.Authenticated) {
      return baseUserContext;
    }

    /*
     * An override may switch the active restaurant to one belonging to a
     * different supplier. Permissions and other supplier-scoped settings are
     * derived from the originally selected supplier's employee record, so
     * re-derive them here to reflect the overridden supplier.
     */
    const overriddenRelationship = overrides.selectedRestaurantRelationship;
    if (
      overriddenRelationship &&
      overriddenRelationship.supplierUUID !==
        baseUserContext.selectedRestaurantRelationship.supplierUUID
    ) {
      return {
        ...baseUserContext,
        ...deriveSupplierScopedUserFields({
          selectedRestaurantRelationship: overriddenRelationship,
          supplierEmployeeMap: baseUserContext.supplierEmployeeMap,
          supplierMap: baseUserContext.supplierMap,
          supplierUUID: overriddenRelationship.supplierUUID,
        }),
        // Preserve any explicit overrides over the re-derived values
        ...overrides,
      };
    }

    return {
      ...baseUserContext,
      ...overrides,
    };
  }, [baseUserContext, overrides]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
